import { BaseValidationRule, validationRule } from "@aurelia/validation";
import { kontonummer } from "./kontonummer";
import { validateAccountNumber } from "./account-number";
import { validateBicAndAccountNumber } from "./iban";
import { toDate } from "../common/dates";

function getIndexModuloBool(is7NumberBg, index) {
  if (is7NumberBg) {
    return index % 2 !== 0;
  }

  return index % 2 === 0;
}

// A rule is used as `.satisfiesRule(new Luhn())`, with the names of the other fields as arguments
// where the rule compares fields: `.satisfiesRule(new Kontonummer("clearingField"))`.
function defineRule(defaultMessage, check) {
  const aliases = defaultMessage ? [{ name: undefined, defaultMessage }] : [];

  @validationRule({ aliases })
  class NamedRule extends BaseValidationRule {
    constructor(...args) {
      super();
      this.args = args;
    }

    execute(value, object) {
      return check(value, object, ...this.args);
    }
  }

  return NamedRule;
}

function ssnCheck(value) {
  if (!value || value.length == 0) {
    console.log("Value is undefined or length zero");
    return false;
  }

  let ssn = value
    .replace(/\D/g, "") // strip out all but digits
    .split("") // convert string to array
    .reverse(); // reverse order for Luhn

  ssn = ssn.slice(0, 10); // keep only 10 digits (i.e. 1977 becomes 77)

  // verify we got 10 digits, otherwise it is invalid
  if (ssn.length != 10) {
    console.log("ssn is somewhat not 10 digits");
    return false;
  }

  var sum = ssn
    // convert to number
    .map(function (n) {
      return Number(n);
    })
    // perform arithmetic and return sum
    .reduce(function (previous, current, index) {
      // multiply every other number with two
      if (index % 2) current *= 2;
      // if larger than 10 get sum of individual digits (also n-9)
      if (current > 9) current -= 9;
      // sum it up
      return previous + current;
    });

  // sum must be divisible by 10
  console.log("Sum modulu 10 is:", sum % 10);
  return 0 === sum % 10;
}

export const DateIsSameOrAfter = defineRule("${$displayName} must be a Date.", (value, obj, otherPropertyName) => {
  // An empty date is for required() to report. No date at all is read as now, and two nows taken a moment
  // apart would make the rule pass or fail by chance.
  if (!value) return true;
  let date = obj[otherPropertyName];
  return toDate(value) >= toDate(date);
});

export const IsSameAs = defineRule("${$displayName} must be same.", (value, obj, otherPropertyName) => {
  let otherValue = obj[otherPropertyName];
  return value == otherValue;
});

export const DigitSequence = defineRule("${$displayName} is not sequence of digits", (value) => {
  let isValid = true;
  for (let d of value) {
    if (isNaN(parseInt(d))) {
      isValid = false;
    }
  }
  return isValid;
});

export const Kontonummer = defineRule("${$displayName} not valid", (value, obj, otherPropertyName) => {
  // kontonummer answers with the bank and the parts of a valid number, and the validator takes only true or false.
  return Boolean(kontonummer(`${obj[otherPropertyName]}${value}`));
});

// Like Kontonummer but validates the clearing number and account number as
// separate fields (otherPropertyName is the clearing field, value the
// account). Used by the payment-fields component; see ./account-number.js.
export const AccountNumber = defineRule("${$displayName} not valid", (value, obj, otherPropertyName) => {
  return validateAccountNumber(obj[otherPropertyName], value);
});

export const ValidateBicAndAccount = defineRule("${$displayName} not valid", (value, obj, otherPropertyName) => {
  return validateBicAndAccountNumber(obj[otherPropertyName], value);
});

export const Luhn = defineRule("Bad luhn checksum.", (value) => {
  if (!value) return false;

  let num = value.replace(/[\s-]/g, "");
  let arr = (num + "").split("").map((x) => parseInt(x));

  let lastDigit = arr.splice(arr.length - 1, 1)[0];
  let sum = 0;

  //validating according to luhn algorythm as specified on BankgiroCentralens homepage.
  if (arr.length === 6 || arr.length === 7) {
    sum = 0;
    arr.map((val, i) => {
      i++;
      if (val !== 0 && getIndexModuloBool(arr.length === 6, i)) {
        sum += val;
      } else if (val !== 0) {
        let tmp = (val * 2) % 9;
        tmp !== 0 ? (sum += tmp) : (sum += 9);
      }
    });

    sum += lastDigit;
    return sum % 10 === 0;
  }

  return false;
});

export const LuhnGeneral = defineRule("Bad luhn checksum.", (value) => {
  let num = value.replace(/[\s-]/g, "");
  let arr = (num + "").split("").map((x) => parseInt(x));

  let lastDigit = arr.splice(arr.length - 1, 1)[0];
  let sum = 0;
  //Doesn't require specific length of the number. PG number can be shorter than BG
  if (arr.length > 1) {
    sum = 0;
    arr.map((val, i) => {
      i++;
      if (val !== 0 && getIndexModuloBool(!(arr.length & 1), i)) {
        sum += val;
      } else if (val !== 0) {
        let tmp = (val * 2) % 9;
        tmp !== 0 ? (sum += tmp) : (sum += 9);
      }
    });

    sum += lastDigit;
    return sum % 10 === 0;
  }

  return false;
});

// The pattern of Aurelia's deprecated `.email()`, so the forms accept the same addresses as before.
const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const Email = defineRule("${$displayName} is not a valid email.", (value) => {
  return value === null || value === undefined || value.length === 0 || emailPattern.test(value);
});

export const InternationalPhoneNumber = defineRule(null, (value) => {
  var patt = new RegExp(/^\+(?:[0-9] ?){6,14}[0-9]$/);
  return patt.test(value);
});

export const ValidSsnOstersjo = defineRule(null, (value) => {
  if (!value || value.length == 0) {
    console.log("Value is undefined or length zero");
    return false;
  }

  if (value.toLowerCase().endsWith("xxxx")) {
    console.log("Value ends with xxxx");
    return true;
  }

  let ssn = value
    .replace(/\D/g, "") // strip out all but digits
    .split("") // convert string to array
    .reverse(); // reverse order for Luhn

  ssn = ssn.slice(0, 10); // keep only 10 digits (i.e. 1977 becomes 77)

  // verify we got 10 digits, otherwise it is invalid
  if (ssn.length != 10) {
    console.log("ssn is somewhat not 10 digits");
    return false;
  }

  console.log("SSN array is:", ssn);

  var sum = ssn
    // convert to number
    .map(function (n) {
      return Number(n);
    })
    // perform arithmetic and return sum
    .reduce(function (previous, current, index) {
      // multiply every other number with two
      if (index % 2) current *= 2;
      // if larger than 10 get sum of individual digits (also n-9)
      if (current > 9) current -= 9;
      // sum it up
      return previous + current;
    });

  // sum must be divisible by 10
  console.log("Sum modulu 10 is:", sum % 10);
  return 0 === sum % 10;
});

export const Ssn = defineRule(null, (value) => {
  var patt = new RegExp(/^\d{0,2}[0-9]{6}[-|+]?[0-9]{4}$/);
  if (!patt.test(value)) {
    return false;
  }
  return ssnCheck(value);
});

export const SsnDash = defineRule(null, (value) => {
  var patt = new RegExp(/^\d{0,2}[0-9]{6}[-|+]{1}[0-9]{4}$/);
  if (!patt.test(value)) {
    return false;
  }
  return ssnCheck(value);
});

export const Ssn12 = defineRule(null, (value) => {
  var patt = new RegExp(/^[0-9]{8}[-|+]?[0-9]{4}$/);
  if (!patt.test(value)) {
    return false;
  }
  return ssnCheck(value);
});

export const OrgNumber = defineRule(null, (value) => {
  var patt = new RegExp(/^[0-9]{6}-?[0-9]{4}$/);
  if (!patt.test(value)) {
    return false;
  }
  return ssnCheck(value);
});
